import {
  ACTION,
  CustomerLedgerUpdateTypeEnum,
  IconName,
  getAgGridColId,
  useDefaultColumns,
  type Column,
  type ColumnDef,
  type CustomerBalance,
} from '@talos/kyoko';
import type { IRowNode } from 'ag-grid-community';
import { compact } from 'lodash-es';
import { useMemo } from 'react';
import { useTheme } from 'styled-components';
import { useRoleAuth } from '../../../hooks';
import { OrgConfigurationKey, useOrgConfiguration } from '../../../providers';
import { useDisplaySettings } from '../../../providers/DisplaySettingsProvider';
import type { CustomerBalancesColumn } from './types';

interface UseCustomerBalancesColumnsParams {
  defaultColumns: CustomerBalancesColumn[];
  onTransactionPrime: (customerBalance: CustomerBalance, transactionType: CustomerLedgerUpdateTypeEnum) => void;
  hideBlotterActions?: boolean;
}

export function useCustomerBalancesColumns({
  defaultColumns,
  onTransactionPrime,
  hideBlotterActions = false,
}: UseCustomerBalancesColumnsParams) {
  const { homeCurrency } = useDisplaySettings();
  const theme = useTheme();
  const { isAuthorized } = useRoleAuth();
  const { getConfig } = useOrgConfiguration();

  const columnDefinitions = useMemo(() => {
    return new Map<string, Column>(
      compact([
        {
          type: 'customer',
          field: 'Counterparty',
          title: 'Customer',
          initialWidth: 150,
          enableRowGroup: true,
        },
        {
          type: 'currency',
          field: 'Currency',
          initialWidth: 125,
          sortable: true,
          params: {
            colorful: true,
          },
          enableRowGroup: true,
        },
        {
          type: 'marketAccountSourceAccountID',
          field: 'MarketAccount',
          initialWidth: 150,
          enableRowGroup: true,
        },
        {
          type: 'size',
          field: 'Equivalent.Amount',
          params: { currencyField: 'Equivalent.Currency', highlightNegative: true },
          title: `Amount (${homeCurrency})`,
          initialWidth: 200,
          sortable: true,
          aggregate: true,
        },
        {
          type: 'size',
          field: 'Amount',
          params: { currencyField: 'Currency', highlightNegative: true },
          initialWidth: 200,
          sortable: true,
          aggregate: true,
        },
        {
          type: 'size',
          field: 'AvailableAmount',
          title: 'Available Amount',
          params: { currencyField: 'Currency', highlightNegative: true },
          initialWidth: 200,
          sortable: true,
          aggregate: true,
        },
        {
          type: 'size',
          field: 'OutstandingBuy',
          title: 'Outstanding Buy',
          params: { currencyField: 'Currency', highlightNegative: true },
          initialWidth: 200,
          sortable: true,
          aggregate: true,
        },
        {
          type: 'size',
          field: 'OutstandingSell',
          title: 'Outstanding Sell',
          params: { currencyField: 'Currency', highlightNegative: true },
          initialWidth: 200,
          sortable: true,
          aggregate: true,
        },
        {
          type: 'size',
          field: 'PendingWithdrawal',
          title: 'Pending Withdrawal',
          params: { currencyField: 'Currency', highlightNegative: true },
          initialWidth: 200,
          sortable: true,
          initialHide: true,
          aggregate: true,
        },
        {
          type: 'size',
          field: 'UnsettledAmount',
          title: 'Unsettled Amount',
          params: { currencyField: 'Currency', highlightNegative: true },
          initialWidth: 200,
          initialHide: true,
          sortable: true,
          aggregate: true,
        },
        !hideBlotterActions && isAuthorized(ACTION.DEALER_TRADING)
          ? {
              type: 'iconButton',
              id: 'deposit-button',
              initialPinned: 'right',
              initialWidth: 50,
              params: {
                alignment: 'rightAligned',
                onClick: ({ node }: { node: IRowNode<CustomerBalance> }) => {
                  if (node.data) {
                    onTransactionPrime?.(node.data, CustomerLedgerUpdateTypeEnum.Deposit);
                  }
                },
                icon: IconName.Download,
                title: 'Deposit',
              },
              frozen: true,
              suppressColumnsToolPanel: true,
            }
          : undefined,
        !hideBlotterActions && isAuthorized(ACTION.DEALER_TRADING)
          ? {
              type: 'iconButton',
              id: 'withdraw-button',
              initialPinned: 'right',
              initialWidth: 50,
              params: {
                alignment: 'rightAligned',
                onClick: ({ node }: { node: IRowNode<CustomerBalance> }) => {
                  if (node.data) {
                    onTransactionPrime?.(node.data, CustomerLedgerUpdateTypeEnum.Withdraw);
                  }
                },
                icon: IconName.Upload,
                title: 'Withdraw',
              },
              frozen: true,
              suppressColumnsToolPanel: true,
            }
          : undefined,
        !hideBlotterActions &&
        (isAuthorized(ACTION.EDIT_CUSTOMER_BALANCE) || getConfig(OrgConfigurationKey.AllowSetCustomerBalance, false))
          ? {
              type: 'iconButton',
              id: 'set-button',
              initialPinned: 'right',
              initialWidth: 50,
              params: {
                alignment: 'rightAligned',
                onClick: ({ node }: { node: IRowNode }) => {
                  if (node.data) {
                    onTransactionPrime?.(node.data, CustomerLedgerUpdateTypeEnum.Set);
                  }
                },
                icon: IconName.Pencil,
                color: theme.backgroundPrimary,
                title: 'Set',
              },
              frozen: true,
              suppressColumnsToolPanel: true,
            }
          : undefined,
      ] satisfies (ColumnDef<CustomerBalance> | undefined)[]).map(c => [getAgGridColId(c), c])
    );
  }, [getConfig, hideBlotterActions, homeCurrency, isAuthorized, theme.backgroundPrimary, onTransactionPrime]);

  const defaultColumnsWithButtons = useMemo(() => {
    const columns: CustomerBalancesColumn[] = [...defaultColumns];
    if (!hideBlotterActions) {
      columns.push('withdraw-button', 'deposit-button', 'set-button');
    }
    return columns;
  }, [defaultColumns, hideBlotterActions]);

  return useDefaultColumns(defaultColumnsWithButtons, columnDefinitions);
}
